import { useState } from "react";

import type { AllEOutput, CheckStatus } from "../../../functions/_lib/alle-check/types";
import { combineThreeCheckVerdict } from "./recommender-input";
import { HybridSavingsCard } from "./HybridSavingsCard";
import { HelpIcon } from "./CheckForm";
import { localeOf, useLang, useT, type Lang } from "../../i18n/language";
import type { Headline, BestChoice } from "../../lib/alle-check-client";
import type { HybridSavingsSummary } from "../../../functions/_lib/savings";

const STAT_HELP: Record<string, string> = {
  warmtevraag:
    "Jaarlijkse energievraag in kWh, afgeleid van gasverbruik of EP1.",
  capaciteitsdekking:
    "Hoeveel procent van de piekvraag bij -7 graden de geselecteerde pomp dekt.",
  aanbevolen_pomp:
    "Solo of Duo, op basis van warmtevraag en bestaand afgiftesysteem.",
  max_binnentemp:
    "Geschatte hoogste haalbare binnentemperatuur op een -7 graden dag.",
};

const CHECK_HELP: Record<string, string> = {
  "Woningtype":
    "Blokkeert appartementen zonder VVE-toestemming en incompatibele heating-types (luchtverwarming, stadsverwarming).",
  "Warmtevraag dekking":
    "Vergelijkt jaarlijkse warmtevraag met capaciteit van Solo / Duo bij -7 graden buiten.",
  "Watertemperatuur":
    "Controleert of het afgiftesysteem voldoende vermogen levert op W35 / W45 / W55.",
  "Thermostaatgedrag":
    "Zone-thermostaten en grote dag/nacht-setbacks zijn ongunstig voor een warmtepomp.",
  "Isolatie & label":
    "Bij energielabel D-G is All-Electric meestal niet geschikt zonder extra isolatie.",
};

interface Props {
  result: AllEOutput;
  hybrid?: HybridSavingsSummary | null;
  headline?: Headline;
  submissionId?: string;
  onReset?: () => void;
}

const VERDICT_LABEL: Record<CheckStatus, string> = {
  geschikt: "Geschikt",
  extra_controle: "Extra controle",
  ongeschikt: "Niet geschikt",
};

const VERDICT_DESCRIPTION: Record<CheckStatus, string> = {
  geschikt: "Op basis van de ingevulde gegevens is deze woning geschikt voor een Quatt All-Electric installatie.",
  extra_controle:
    "Deze woning kan geschikt zijn, maar er zijn punten die extra aandacht nodig hebben. Bekijk de details hieronder.",
  ongeschikt:
    "Op basis van de ingevulde gegevens is deze woning niet geschikt voor All-Electric. Een Quatt Hybrid is een passender configuratie, of de woning kan eerst aanvullend geïsoleerd worden.",
};

const PUMP_LABEL: Record<NonNullable<AllEOutput["check1_heat_demand"]["recommended_pump"]>, string> = {
  single: "All-Electric Solo",
  duo: "All-Electric Duo",
  not_possible: "Niet mogelijk",
};

const PUMP_DESCRIPTION: Record<NonNullable<AllEOutput["check1_heat_demand"]["recommended_pump"]>, string> = {
  single: "Eén buitenunit volstaat voor de berekende warmtevraag bij -7°C.",
  duo: "De warmtevraag vraagt om een Duo-configuratie (twee buitenunits) bij -7°C.",
  not_possible: "Geen All-Electric configuratie haalt de warmtevraag bij -7°C. Eerst aanvullende isolatie of een Quatt Hybrid is een passender route.",
};

function statusKind(status: CheckStatus): "is-good" | "is-warn" | "is-bad" {
  if (status === "geschikt") return "is-good";
  if (status === "extra_controle") return "is-warn";
  return "is-bad";
}

function fallbackPumpFromCoverage(
  coveragePct: number,
): "single" | "duo" | "not_possible" {
  if (coveragePct >= 100) return "single";
  if (coveragePct >= 70) return "duo";
  return "not_possible";
}

function formatNumber(n: number, lang: Lang): string {
  return new Intl.NumberFormat(localeOf(lang), { maximumFractionDigits: 0 }).format(n);
}

const VERDICT_BG: Record<CheckStatus, string> = {
  geschikt: "linear-gradient(135deg, #E5E9E1 0%, #F0F3EB 100%)",
  extra_controle: "linear-gradient(135deg, #fff8e6 0%, #fffbf0 100%)",
  ongeschikt: "linear-gradient(135deg, #fde8e4 0%, #fff5f3 100%)",
};

export function CheckResults({ result, hybrid, headline, submissionId, onReset }: Props) {
  const t = useT();
  const { lang } = useLang();
  const verdict = combineThreeCheckVerdict(result);
  const heat = result.check1_heat_demand;
  const pump = heat.recommended_pump ?? fallbackPumpFromCoverage(heat.coverage_pct);
  const pumpUnknown = heat.coverage_pct === 0;
  const [openStat, setOpenStat] = useState<string | null>(null);
  const [openCheck, setOpenCheck] = useState<string | null>(null);
  const [view, setView] = useState<BestChoice>(headline?.best_choice ?? "all_electric");
  const toggleStat = (k: string) => setOpenStat((p) => (p === k ? null : k));
  const toggleCheck = (k: string) => setOpenCheck((p) => (p === k ? null : k));

  // Solo -> Duo upgrade toggle. Only relevant when the engine recommends "single"
  // and the verdict is not ongeschikt. The engine's recommendation stays the
  // source of truth; this is a user-driven UI override so they can compare.
  const showPumpChoice = pump === "single" && verdict !== "ongeschikt";
  const [selectedPump, setSelectedPump] = useState<"single" | "duo">("single");
  const effectivePump: NonNullable<AllEOutput["check1_heat_demand"]["recommended_pump"]> =
    showPumpChoice ? selectedPump : pump;
  const isDuoUpgrade = showPumpChoice && selectedPump === "duo";

  const verdictTitle = pumpUnknown
    ? VERDICT_LABEL[verdict]
    : isDuoUpgrade
      ? "All-Electric Duo (upgrade)"
      : PUMP_LABEL[effectivePump];

  const pumpDescription = isDuoUpgrade
    ? "Solo zou volstaan, maar Duo geeft meer vermogen, snellere warmwater en hogere subsidie."
    : PUMP_DESCRIPTION[effectivePump];

  const checks = [
    { name: "Woningtype", status: result.check0_exclusions.status, detail: result.check0_exclusions.detail },
    { name: "Warmtevraag dekking", status: result.check1_heat_demand.status, detail: result.check1_heat_demand.detail },
    { name: "Watertemperatuur", status: result.check2_water_temp.status, detail: result.check2_water_temp.detail },
    { name: "Thermostaatgedrag", status: result.check3_thermostat.status, detail: result.check3_thermostat.detail },
    { name: "Isolatie & label", status: result.check4_insulation.status, detail: result.check4_insulation.detail },
  ];

  const bestChoice = headline?.best_choice;
  const formatNl = (n: number) => formatNumber(n, lang);
  const bestChoiceTitle =
    bestChoice === "all_electric"
      ? "Beste keuze: All-Electric"
      : "Beste keuze: Quatt Hybrid";
  const bestChoiceCopy =
    bestChoice === "all_electric"
      ? "Op basis van de ingevulde gegevens is deze woning geschikt voor All-Electric. Hieronder de aanbevolen configuratie, met de Hybride besparing als alternatief."
      : "Op basis van de ingevulde gegevens is een Quatt Hybrid hier de passende keuze. Bekijk de geschatte besparing hieronder; All-Electric vraagt eerst extra aandacht.";

  // Advice toggle: only when both advices exist (hybrid is null on the BENG1 route).
  const hasToggle = Boolean(bestChoice && hybrid);
  const showAlle = !hybrid || view === "all_electric";
  const showHybrid = Boolean(hybrid) && view === "hybrid";

  return (
    <div className="results-layout">
      {bestChoice && (
        <section
          className="card"
          style={{
            background: bestChoice === "all_electric" ? VERDICT_BG.geschikt : "linear-gradient(135deg, #E5EEF3 0%, #F2F8FB 100%)",
            borderRadius: 20.16,
            padding: "32px 40px",
            opacity: 1,
            transform: "none",
            animation: "none",
          }}
        >
          <div className="eyebrow">{t("Advies")}</div>
          <h1 className="h1" style={{ marginBottom: 12 }}>{t(bestChoiceTitle)}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>{t(bestChoiceCopy)}</p>
          {bestChoice === "hybrid" && hybrid && (
            <p className="subtitle" style={{ marginTop: 12, marginBottom: 0, fontSize: 15 }}>
              {t("Geschatte besparing")}: {formatNl(hybrid.gas_m3_saved)} m³ {t("gas")} {t("per jaar")} ({hybrid.recommended_product}).
            </p>
          )}
        </section>
      )}

      {hasToggle && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-btn${view === "all_electric" ? " active" : ""}`}
              onClick={() => setView("all_electric")}
              aria-pressed={view === "all_electric"}
            >
              {t("All-Electric")}
            </button>
            <button
              type="button"
              className={`view-toggle-btn${view === "hybrid" ? " active" : ""}`}
              onClick={() => setView("hybrid")}
              aria-pressed={view === "hybrid"}
            >
              {t("Hybride")}
            </button>
          </div>
        </div>
      )}

      {showHybrid && hybrid && <HybridSavingsCard hybrid={hybrid} />}

      {showAlle && (
      <>
      {showPumpChoice && (
        <section
          className="card"
          style={{
            padding: "28px 32px",
            opacity: 1,
            transform: "none",
            animation: "none",
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 8 }}>{t("Vergelijk de opties")}</div>
          <p className="card-subtitle" style={{ marginTop: 0, marginBottom: 20 }}>
            {t("Op basis van je gegevens volstaat een Solo. Wil je liever meer comfort, een hogere subsidie en een langere levensduur. Bekijk dan ook de Duo-upgrade.")}
          </p>
          <div className="pump-choice-grid">
            <button
              type="button"
              className={`pump-choice-card${selectedPump === "single" ? " selected" : ""}`}
              onClick={() => setSelectedPump("single")}
              aria-pressed={selectedPump === "single"}
            >
              <div className="pump-choice-badge solo">{t("Voldoende")}</div>
              <h3 className="pump-choice-title">All-Electric Solo</h3>
              <p className="pump-choice-sub">{t("Eén buitenunit. Past binnen de berekende warmtevraag.")}</p>
              <ul className="pump-choice-benefit-list">
                <li>{t("Lagere investering")}</li>
                <li>{t("Kleinere voetafdruk buiten")}</li>
                <li>{t("Compact installatieproces")}</li>
              </ul>
            </button>
            <button
              type="button"
              className={`pump-choice-card${selectedPump === "duo" ? " selected" : ""}`}
              onClick={() => setSelectedPump("duo")}
              aria-pressed={selectedPump === "duo"}
            >
              <div className="pump-choice-badge duo">{t("Aanbevolen upgrade")}</div>
              <h3 className="pump-choice-title">All-Electric Duo</h3>
              <p className="pump-choice-sub">{t("Twee buitenunits. Meer vermogen voor extra comfort.")}</p>
              <ul className="pump-choice-benefit-list">
                <li>{t("Hogere subsidie")}</li>
                <li>{t("Meer vermogen, sneller op temperatuur")}</li>
                <li>{t("Snellere warmwater-opwarming")}</li>
                <li>{t("Langere levensduur door minder belasting per unit")}</li>
              </ul>
            </button>
          </div>
        </section>
      )}

      <section
        className="card"
        style={{
          background: VERDICT_BG[verdict],
          borderRadius: 20.16,
          padding: "40px 48px",
          opacity: 1,
          transform: "none",
          animation: "none",
        }}
      >
        <div className="eyebrow">{t("All-Electric aanbeveling")}</div>
        <h1 className="h1" style={{ marginBottom: 12 }}>
          {t(verdictTitle)}
        </h1>
        <p className="subtitle" style={{ marginBottom: 16 }}>{t(VERDICT_DESCRIPTION[verdict])}</p>
        {!pumpUnknown && <p className="subtitle" style={{ marginBottom: 0, fontSize: 15 }}>{t(pumpDescription)}</p>}

        {heat.coverage_pct > 0 && (
          <div className="results-stats-card" style={{ marginTop: 28, background: "rgba(255,255,255,0.7)" }}>
            {[
              { key: "warmtevraag", label: "Warmtevraag", value: `${formatNumber(heat.energy_demand_kwh, lang)} kWh` },
              { key: "capaciteitsdekking", label: "Capaciteitsdekking", value: `${heat.coverage_pct}%` },
              { key: "aanbevolen_pomp", label: "Aanbevolen pomp", value: t(PUMP_LABEL[effectivePump]) },
              {
                key: "max_binnentemp",
                label: "Max. binnentemp bij -7°C",
                value:
                  heat.estimated_max_indoor_temp_c !== null && heat.estimated_max_indoor_temp_c !== undefined
                    ? `${heat.estimated_max_indoor_temp_c.toFixed(1)}°C`
                    : "-",
              },
            ].map((stat) => {
              const isOpen = openStat === stat.key;
              return (
                <div className="stat-item" key={stat.key}>
                  <div className="stat-label-row">
                    <div className="stat-label" style={{ color: "var(--muted)" }}>{t(stat.label)}</div>
                    <HelpIcon open={isOpen} onClick={() => toggleStat(stat.key)} label={`${t("Uitleg")} ${t(stat.label)}`} />
                  </div>
                  <div className="stat-value" style={{ color: "var(--ink)" }}>{stat.value}</div>
                  <div className={`help-tooltip ${isOpen ? "help-panel-open" : ""}`}>{t(STAT_HELP[stat.key] ?? "")}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {result.check0_exclusions.blocked_reasons.length > 0 && (
        <section className="card" style={{ opacity: 1, transform: "none", animation: "none" }}>
          <div className="section-header">
            <span className="step-badge" style={{ background: "var(--danger)" }}>!</span>
            <h2 className="section-title">{t("Blokkeringen")}</h2>
          </div>
          <ul className="disclaimers">
            {result.check0_exclusions.blocked_reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="card" style={{ opacity: 1, transform: "none", animation: "none" }}>
        <div className="section-header">
          <span className="step-badge" style={{ background: "var(--ink)" }}>•</span>
          <h2 className="section-title">{t("Per-check uitleg")}</h2>
        </div>
        <div className="checks-grid">
          {checks.map((c) => {
            const kind = statusKind(c.status);
            const isOpen = openCheck === c.name;
            const help = CHECK_HELP[c.name];
            return (
              <div key={c.name} className={`check-card ${kind}`}>
                <span className={`status-pill ${kind}`}>{t(VERDICT_LABEL[c.status])}</span>
                <div className="check-label-row" style={{ marginTop: 4 }}>
                  <div className="check-card-name">{t(c.name)}</div>
                  {help && (
                    <HelpIcon open={isOpen} onClick={() => toggleCheck(c.name)} label={`${t("Uitleg")} ${t(c.name)}`} />
                  )}
                </div>
                <div className="check-card-detail">{c.detail}</div>
                {help && (
                  <div className={`help-tooltip ${isOpen ? "help-panel-open" : ""}`}>{t(help)}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {result.disclaimers.length > 0 && (
        <section className="card warm" style={{ opacity: 1, transform: "none", animation: "none" }}>
          <div className="section-header">
            <span className="step-badge" style={{ background: "var(--teal)" }}>i</span>
            <h2 className="section-title">{t("Aandachtspunten")}</h2>
          </div>
          <ul className="disclaimers">
            {result.disclaimers.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </section>
      )}

      <div className="results-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={async () => {
            try {
              const resp = await fetch("/api/v1/alle-check/pdf", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ result, lang }),
              });
              if (!resp.ok) {
                const txt = await resp.text();
                alert(`${t("PDF kon niet gegenereerd worden:")} ${txt.slice(0, 200)}`);
                return;
              }
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = lang === "en" ? "All-E-recommendation.pdf" : "All-E-aanbeveling.pdf";
              document.body.appendChild(a);
              a.click();
              a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 60_000);
            } catch (err) {
              alert(`${t("PDF kon niet gegenereerd worden:")} ${(err as Error).message}`);
            }
          }}
        >
          Download PDF
        </button>
      </div>

      <EmailPdfBlock result={result} />
      </>
      )}

      {onReset && (
        <div className="results-actions">
          <button type="button" className="btn-secondary" onClick={onReset}>
            {t("Nieuwe aanbeveling")}
          </button>
        </div>
      )}
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailPdfBlock({ result }: { result: AllEOutput }) {
  const t = useT();
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState<string>("");

  const validEmail = EMAIL_RE.test(email.trim());
  const disabled = status === "sending" || !validEmail;

  async function send() {
    setStatus("sending");
    setMessage("");
    try {
      const resp = await fetch("/api/v1/alle-check/email-pdf", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ result, email: email.trim(), lang }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        let detail = txt;
        try {
          const parsed = JSON.parse(txt) as { detail?: string; title?: string };
          detail = parsed.detail ?? parsed.title ?? txt;
        } catch {
          // raw text
        }
        setStatus("error");
        setMessage(detail.slice(0, 240));
        return;
      }
      setStatus("sent");
      setMessage(`${t("Verstuurd naar")} ${email.trim()}.`);
    } catch (err) {
      setStatus("error");
      setMessage((err as Error).message);
    }
  }

  return (
    <section
      className="card"
      style={{ marginTop: 16, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}
    >
      <div style={{ fontWeight: 600 }}>{t("Of stuur de aanbeveling per e-mail")}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t("naam@voorbeeld.nl")}
          value={email}
          disabled={status === "sending"}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status !== "idle") {
              setStatus("idle");
              setMessage("");
            }
          }}
          style={{
            flex: "1 1 240px",
            minWidth: 200,
            padding: "10px 12px",
            border: "1px solid var(--border, #d0d0d0)",
            borderRadius: 8,
            fontSize: 15,
          }}
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={disabled}
          onClick={send}
          style={{ opacity: disabled ? 0.55 : 1 }}
        >
          {status === "sending" ? t("Versturen...") : t("Verstuur per e-mail")}
        </button>
      </div>
      {status === "sent" && (
        <div style={{ color: "#1f7a3a", fontSize: 14 }}>{message}</div>
      )}
      {status === "error" && (
        <div style={{ color: "#b3261e", fontSize: 14 }}>{t("Kon niet versturen:")} {message}</div>
      )}
    </section>
  );
}
